import { inject, Injectable, Type } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { Observable, from, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ModalConfig, ModalResult } from '../types';

@Injectable({
  providedIn: 'root',
})
export class UniversalModalService {
  private modalService = inject(NgbModal);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  openModal<TData = Record<string, unknown>, TResult = unknown>(
    config: ModalConfig<TData>,
  ): Observable<ModalResult<TResult>> {
    const { component, data, options, routeParam } = config;

    if (routeParam) {
      this.updateRouteWithParam(routeParam.name, routeParam.value);
    }

    const modalRef = this.modalService.open(component, options);

    if (data) {
      Object.assign(modalRef.componentInstance, data);
    }

    return from(modalRef.result).pipe(
      map((result: TResult) => ({
        dismissed: false,
        data: result,
      })),
      catchError((reason: unknown) => {
        return of({
          dismissed: true,
          data: reason as TResult,
        });
      }),
      map((result: ModalResult<TResult>) => {
        if (routeParam) {
          this.clearParamFromRoute(routeParam.name);
        }
        return result;
      }),
    );
  }

  checkAndOpenModalFromRoute<
    TData = Record<string, unknown>,
    TResult = unknown,
  >(
    component: Type<unknown>,
    paramName: string,
    dataMapper?: (paramValue: string) => TData,
    options?: NgbModalOptions,
  ): Observable<ModalResult<TResult>> | null {
    const paramValue = this.route.snapshot.queryParams[paramName] as
      | string
      | undefined;

    if (!paramValue) {
      return null;
    }

    const data = dataMapper
      ? dataMapper(paramValue)
      : ({ [paramName]: paramValue } as TData);

    const modalRef = this.modalService.open(component, options);

    if (data) {
      Object.assign(modalRef.componentInstance, data);
    }

    return from(modalRef.result).pipe(
      map((result: TResult) => ({
        dismissed: false,
        data: result,
      })),
      catchError((reason: unknown) => {
        return of({
          dismissed: true,
          data: reason as TResult,
        });
      }),
      map((result: ModalResult<TResult>) => {
        this.clearParamFromRoute(paramName);
        return result;
      }),
    );
  }

  private updateRouteWithParam(param: string, value: string): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { [param]: value },
        queryParamsHandling: 'merge',
      })
      .then();
  }

  private clearParamFromRoute(param: string): void {
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: { [param]: null },
        queryParamsHandling: 'merge',
      })
      .then();
  }
}
